/**
 * The "Ready to sell?" card in the Bookings side menu.
 *
 * Design: `WSB2.0 Upgrade-Downgrade`, "Approaching limit notification" — a
 * lavender card pinned under the menu items, with a lightning glyph, a
 * persuasive title, a description that states both prices, and an Upgrade
 * button.
 *
 * That is Gravity's condensed `upgrade` notice. The brand stylesheet gives the
 * variant its lavender surface (`--color-signal-upgrade-light`) and its own
 * flash-in-a-circle glyph, and `condensed` supplies the tighter padding, gap and
 * type the rail needs — all of it redefined per white-label brand, so none of it
 * is spelled out here. The card sits inside the `gv-activated` subtree that
 * PageLayout wraps around FlatSideMenu, which is what scopes those rules.
 *
 * Presentational only. Both prices arrive already formatted, because the card
 * has no opinion about currency or billing period — it only refuses to render a
 * blank amount, which is the caller's job to prevent.
 */
import React from 'react';
import { Notice, Button } from '@shore/react-gravity';
import { createGlobalStyle } from 'styled-components';
import { t } from '../../utils/i18n';
import { gravityFontSize } from '../../shared/styles/gravity-type';

/**
 * Wrapper class the narrowing rules below hang off.
 *
 * `Notice` accepts no `className`, so there is nothing on the notice itself to
 * scope to. Scoping to `.gv-sidebar` instead would be broader than this card —
 * `Locations` renders its own `gv-sidebar` — so the rules would reach notices
 * they were never written for.
 */
const CARD_CLASS = 'as-bookings-upgrade-card';

/**
 * Notice lays itself out as one row — glyph | copy | action — which suits a
 * page-width banner. The side rail gives it ~200px, which leaves the copy a
 * sliver once the non-shrinking glyph and button take their width.
 *
 * Gravity already solves this, but only under `@media (max-width: 599px)`, and a
 * media query keys off the viewport — the rail is narrow on a wide screen, so
 * none of it applies. These are that media block's own declarations, re-applied
 * by container instead, and weighted to outrank Gravity rather than tie with it
 * — see `shared/styles/gravity-type` for why a tie is not safe here.
 *
 * Global because the markup they target is inside `Notice`'s own tree, which a
 * styled wrapper cannot reach with predictable weight — not because the rules
 * are meant to travel. Injected only while the card is mounted.
 */
const SidebarNoticeStyles = createGlobalStyle`
  /* Outranks .gv-activated .gv-notice (0,2,0). */
  .gv-activated .${CARD_CLASS} .gv-notice {
    flex-wrap: wrap;
  }

  /* Outranks .gv-activated .gv-notice .gv-notice-content (0,3,0). */
  .gv-activated .${CARD_CLASS} .gv-notice .gv-notice-content {
    width: 100%;
  }

  /* Outranks .gv-activated .gv-notice .gv-button (0,3,0). */
  .gv-activated .${CARD_CLASS} .gv-notice .gv-button {
    margin-top: var(--size-sm);
    /* The one departure from Gravity's narrow-viewport block, which leaves the
       button its intrinsic width: the design draws it filling the card. */
    width: 100%;
  }

  /* The size Gravity itself asks for, restated in px. Outranks the (0,4,0)
     condensed rule it replaces. */
  .gv-activated .${CARD_CLASS} .gv-notice.gv-mode-condensed .gv-notice-content {
    font-size: ${gravityFontSize('--font-size-caption-lg')};
  }

  .gv-activated .${CARD_CLASS} .gv-notice .gv-button {
    font-size: ${gravityFontSize('--font-size-text-sm')};
  }
`;

export interface BookingsUpgradeSidebarCardProps {
  /** Discounted first-year price, formatted for display, e.g. `"€4.99/mo"`. */
  introductoryPrice: string;
  /** Price the subscription renews at, formatted the same way. */
  renewalPrice: string;
  /** Sends the merchant to checkout. */
  onUpgrade: () => void;
}

export function BookingsUpgradeSidebarCard({
  introductoryPrice,
  renewalPrice,
  onUpgrade,
}: BookingsUpgradeSidebarCardProps) {
  return (
    <div className={CARD_CLASS}>
      <SidebarNoticeStyles />
      <Notice
        type="upgrade"
        condensed
        title={t('bookings-upgrade.sidebar-card.title')}
        action={
          <Button variant="neutral" onClick={onUpgrade}>
            {t('bookings-upgrade.upgrade')}
          </Button>
        }
      >
        {t('bookings-upgrade.sidebar-card.description')}{' '}
        {t('bookings-upgrade.price', { introductoryPrice, renewalPrice })}
      </Notice>
    </div>
  );
}
